import type { NavigateFunction } from "react-router-dom";
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import FilterListIcon from "@mui/icons-material/FilterList";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { formatDate } from "helpers/formatDate";
import { formatCurrency } from "helpers/utils";
import type { IPedidoGerenciarListItem } from "interfaces/loja";
import { chipColorStatus, labelStatus, totalItensPedido } from "utils/pedidos";
import {
  textoTooltipHistoricoPedido,
  tituloResumoHistoricoPedido,
  ultimoHistoricoPedido,
} from "utils/pedidoHistoricoUi";
import {
  canalInscricaoIcon,
  formatCanalInscricaoLabel,
} from "helpers/canalInscricao";

export function createPedidosColumns(
  navigate: NavigateFunction
): GridColDef<IPedidoGerenciarListItem>[] {
  return [
    {
      field: "criadoEm",
      headerName: "Data",
      flex: 0.9,
      minWidth: 158,
      valueGetter: (params) => params.row.criadoEm ?? "",
      renderCell: (params: GridRenderCellParams<IPedidoGerenciarListItem>) => (
        <Typography variant="body2" component="span">
          {params.row.criadoEm
            ? formatDate(params.row.criadoEm, "dd/MM/yyyy HH:mm:ss")
            : "—"}
        </Typography>
      ),
    },
    {
      field: "numero",
      headerName: "Nº pedido",
      flex: 1.1,
      minWidth: 200,
      sortable: false,
      renderCell: (params: GridRenderCellParams<IPedidoGerenciarListItem>) => (
        <Stack spacing={0.25} sx={{ py: 0.5 }}>
          <Typography
            variant="body2"
            component="button"
            type="button"
            onClick={() => navigate(`/loja/pedidos/${params.row.id}`)}
            sx={{
              m: 0,
              p: 0,
              border: "none",
              background: "none",
              cursor: "pointer",
              color: "primary.main",
              textAlign: "left",
              font: "inherit",
              textDecoration: "underline",
              "&:hover": { opacity: 0.85 },
            }}
          >
            Loja: {params.row.numero ?? params.row.id.slice(0, 8)}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontFamily: "monospace" }}
          >
            ID: {params.row.id}
          </Typography>
        </Stack>
      ),
    },
    {
      field: "compradorNome",
      headerName: "Comprador",
      flex: 1.2,
      minWidth: 180,
      valueGetter: (p) => p.row.compradorNome?.trim() ?? "",
      renderCell: (p) => (
        <Typography variant="body2">{p.value || "—"}</Typography>
      ),
    },
    {
      field: "totalItens",
      headerName: "Itens",
      width: 88,
      align: "center",
      headerAlign: "center",
      valueGetter: (p) => totalItensPedido(p.row),
      renderCell: (p) => <Typography variant="body2">{p.value}</Typography>,
    },
    {
      field: "total",
      headerName: "Valor",
      width: 120,
      type: "number",
      valueGetter: (p) => Number(p.row.total ?? 0),
      renderCell: (p) => (
        <Typography variant="body2">{formatCurrency(p.value)}</Typography>
      ),
    },
    {
      field: "custoFrete",
      headerName: "Frete",
      width: 110,
      type: "number",
      valueGetter: (p) => Number(p.row.custoFrete ?? 0),
      renderCell: (p) => (
        <Typography variant="body2">{formatCurrency(p.value)}</Typography>
      ),
    },
    {
      field: "servicoFrete",
      headerName: "Método cotação",
      flex: 0.8,
      minWidth: 120,
      renderHeader: () => (
        <Stack direction="row" alignItems="center" gap={0.5}>
          <FilterListIcon sx={{ fontSize: 16, opacity: 0.6 }} />
          <span>Método cotação</span>
        </Stack>
      ),
      valueGetter: (p) => p.row.servicoFrete?.trim() ?? "",
      renderCell: (p) => (
        <Typography variant="body2">{p.value || "—"}</Typography>
      ),
    },
    {
      field: "canalVenda",
      headerName: "Canal",
      width: 130,
      sortable: false,
      renderHeader: () => (
        <Stack direction="row" alignItems="center" gap={0.5}>
          <FilterListIcon sx={{ fontSize: 16, opacity: 0.6 }} />
          <span>Canal</span>
        </Stack>
      ),
      valueGetter: (p) =>
        formatCanalInscricaoLabel(p.row.canalVenda, p.row.plataformaApp),
      renderCell: (params: GridRenderCellParams<IPedidoGerenciarListItem>) => {
        const label = formatCanalInscricaoLabel(
          params.row.canalVenda,
          params.row.plataformaApp,
        );
        const icon = canalInscricaoIcon(
          params.row.canalVenda,
          params.row.plataformaApp,
        );
        return (
          <MDBox display="flex" alignItems="center" gap={0.75}>
            <Icon fontSize="small" color="action">
              {icon}
            </Icon>
            <MDTypography variant="body2">{label}</MDTypography>
          </MDBox>
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 168,
      renderHeader: () => (
        <Stack direction="row" alignItems="center" gap={0.5}>
          <FilterListIcon sx={{ fontSize: 16, opacity: 0.6 }} />
          <span>Status</span>
        </Stack>
      ),
      valueGetter: (p) => p.row.status,
      renderCell: (p) => (
        <Chip
          label={labelStatus(p.value)}
          size="small"
          color={chipColorStatus(p.value)}
          variant={
            p.value === "ENTREGUE" || p.value === "PAGO" ? "filled" : "outlined"
          }
          sx={{ fontWeight: 600, px: 1, py: 0.5 }}
        />
      ),
    },
    {
      field: "ultimoHistorico",
      headerName: "Último lançamento",
      flex: 1,
      minWidth: 200,
      sortable: false,
      renderHeader: () => (
        <Stack direction="row" alignItems="center" gap={0.5}>
          <FilterListIcon sx={{ fontSize: 16, opacity: 0.6 }} />
          <span>Último lançamento</span>
        </Stack>
      ),
      valueGetter: (p) => {
        const it = ultimoHistoricoPedido(p.row);
        return it ? tituloResumoHistoricoPedido(it) : "";
      },
      renderCell: (p) => {
        const it = ultimoHistoricoPedido(p.row);
        if (!it) {
          return (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          );
        }
        const titulo = tituloResumoHistoricoPedido(it);
        return (
          <Tooltip
            title={textoTooltipHistoricoPedido(it)}
            enterDelay={400}
            componentsProps={{
              tooltip: {
                sx: {
                  whiteSpace: "pre-line",
                  maxWidth: 360,
                },
              },
            }}
          >
            <Typography variant="body2" noWrap sx={{ maxWidth: "100%" }}>
              {titulo}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: "actions",
      headerName: "",
      width: 72,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Tooltip title="Abrir pedido">
          <MDButton
            size="medium"
            variant="text"
            color="dark"
            onClick={() => navigate(`/loja/pedidos/${params.row.id}`)}
            sx={{ minWidth: 0, px: 1 }}
          >
            <Icon fontSize="medium">visibility</Icon>
          </MDButton>
        </Tooltip>
      ),
    },
  ];
}
